// @ts-nocheck
// Single shared "Your teams" list used by /club-dash and the org-admin Teams page.
// Backed by the rle-list-teams-unified edge function. Same UI for every role.
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Pencil, Archive, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import TeamRenameDialog from "@/components/teams/TeamRenameDialog";

type View = 'coaching' | 'registered' | 'org';

type Viewer = {
  show_coaching_registered_toggle: boolean;
  can_view_org: boolean;
  is_admin: boolean;
  active_view: View;
};

type Team = {
  id: string;
  name: string;
  display_name: string | null;
  short_name: string | null;
  stable_slug: string | null;
  coach_relationship: string | null;
  event_count: number;
  can_edit: boolean;
  can_archive_delete: boolean;
  is_archived: boolean;
};

function relationshipBadge(rel: string | null) {
  if (rel === 'primary_coach' || rel === 'head_coach')
    return { label: 'Coach', className: 'bg-emerald-100 text-emerald-700' };
  if (rel === 'registrant')
    return { label: 'Registrant', className: 'bg-amber-100 text-amber-700' };
  if (rel === 'admin')
    return { label: 'Admin', className: 'bg-indigo-100 text-indigo-700' };
  return { label: 'Manager', className: 'bg-slate-100 text-slate-600' };
}

async function authedInvoke(fn: string, body: unknown) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  return invokeEdgeFunction(fn, {
    body,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
}

/**
 * `mode="coach"` renders the coach-safe view: only "Teams I'm coaching" /
 * "Teams I've registered", and never admin edit/archive/delete controls —
 * regardless of the viewer's roles elsewhere in the platform.
 */
export default function TeamsList({ mode = 'admin' }: { mode?: 'coach' | 'admin' } = {}) {
  const coachMode = mode === 'coach';
  const navigate = useNavigate();
  const [view, setView] = useState<View>('coaching');
  const [viewer, setViewer] = useState<Viewer | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit dialog state
  const [editTeam, setEditTeam] = useState<Team | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (nextView: View) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await authedInvoke('rle-list-teams-unified', {
        view: nextView,
      });
      if (fnError || !data?.ok) {
        setError(data?.error || fnError?.message || 'Could not load teams');
        setTeams([]);
        return;
      }
      setViewer(data.data.viewer);
      setTeams(data.data.teams ?? []);
      setView(data.data.viewer?.active_view ?? nextView);
    } catch (e: any) {
      setError(e?.message || 'Could not load teams');
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load('coaching');
  }, [load]);

  const onSelect = (next: View) => {
    if (next === view) return;
    setView(next);
    load(next);
  };

  // Build the segmented options from the viewer flags (never by role name).
  const options: { value: View; label: string }[] = [
    { value: 'coaching', label: "Teams I'm coaching" },
  ];
  if (viewer?.show_coaching_registered_toggle) {
    options.push({ value: 'registered', label: "Teams I've registered" });
  }
  if (viewer?.can_view_org && !coachMode) {
    options.push({ value: 'org', label: 'All org teams' });
  }
  const showToggle = options.length > 1;

  const openEdit = (t: Team) => {
    setEditTeam(t);
  };

  const archiveTeam = async (t: Team) => {
    if (!window.confirm(`Archive "${t.name}"? It will be hidden from the list.`)) return;
    const { data, error: fnError } = await authedInvoke('rle-manage-team', {
      action: 'archive',
      team_id: t.id,
    });
    if (fnError || !data?.ok) {
      toast.error(data?.error?.message || fnError?.message || 'Could not archive team');
      return;
    }
    toast.success('Team archived');
    setTeams((prev) => prev.filter((x) => x.id !== t.id));
  };

  const deleteTeam = async (t: Team, force = false) => {
    if (!force) {
      if (
        !window.confirm(
          `Delete "${t.name}"? This permanently removes the team and cannot be undone.`,
        )
      )
        return;
    }
    const { data, error: fnError } = await authedInvoke('rle-manage-team', {
      action: 'delete',
      team_id: t.id,
      ...(force ? { force: true } : {}),
    });
    if (fnError || !data?.ok) {
      const code = data?.error?.code;
      if (code === 'TEAM_HAS_ROSTER') {
        if (
          window.confirm(
            `${data.error.message}\n\nDelete anyway? This permanently removes the team and its roster.`,
          )
        ) {
          return deleteTeam(t, true);
        }
        return;
      }
      toast.error(data?.error?.message || fnError?.message || 'Could not delete team');
      return;
    }
    toast.success('Team deleted');
    setTeams((prev) => prev.filter((x) => x.id !== t.id));
  };

  const stop = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <TooltipProvider>
      <div className="space-y-3">
      {showToggle && (
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-sm">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onSelect(opt.value)}
              className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                view === opt.value
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : teams.length === 0 ? (
        <p className="text-sm text-slate-500">No teams yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-100 bg-white shadow-sm">
          {teams.map((t) => {
            const teamHref = t.stable_slug ? `/teams/${t.stable_slug}` : `/teams/${t.id}`;
            const badge = relationshipBadge(t.coach_relationship);
            return (
              <li
                key={t.id}
                onClick={() => navigate(teamHref)}
                className={`flex cursor-pointer items-center justify-between gap-3 px-5 py-4 transition-colors hover:bg-slate-50 ${
                  t.is_archived ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium text-slate-900 truncate">{t.name}</span>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge.className}`}
                  >
                    {badge.label}
                  </span>
                  {t.is_archived && (
                    <span className="shrink-0 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                      Archived
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    style={{ backgroundColor: 'rgba(6,182,212,0.15)', color: '#0e7490' }}
                  >
                    {t.event_count} event{t.event_count === 1 ? '' : 's'}
                  </span>
                  {t.can_edit && !coachMode && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={(e) => {
                            stop(e);
                            openEdit(t);
                          }}
                          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                          aria-label="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Edit</TooltipContent>
                    </Tooltip>
                  )}
                  {t.can_archive_delete && !coachMode && (
                    <>
                      <Tooltip>
 <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={(e) => {
                              stop(e);
                              archiveTeam(t);
                            }}
                            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                            aria-label="Archive"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Archive</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={(e) => {
                              stop(e);
                              deleteTeam(t);
                            }}
                            className="rounded-md p-1.5 text-red-500 hover:bg-red-50 hover:text-red-600"
                            aria-label="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Delete</TooltipContent>
                      </Tooltip>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <TeamRenameDialog
        teamId={editTeam?.id ?? null}
        open={!!editTeam}
        onOpenChange={(o) => !o && setEditTeam(null)}
        onSaved={() => load(view)}
      />
    </div>
    </TooltipProvider>
  );
}
